import React from "react";
import { motion } from "framer-motion";
import { Briefcase } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

interface MatterOverviewReportProps {
  data: {
    byStatus: { status: string; count: number }[];
    byPracticeArea: { practiceArea: string; count: number }[];
    total: number;
  } | null;
  loading: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  open: "#10b981",
  closed: "#64748b",
  on_hold: "#f59e0b",
};

const STATUS_LABELS: Record<string, string> = {
  open: "Active",
  closed: "Closed",
  on_hold: "On Hold",
};

const PRACTICE_AREA_COLORS: Record<string, string> = {
  real_estate: "#06b6d4",
  immigration: "#10b981",
  litigation: "#3b82f6",
  corporate: "#8b5cf6",
  family: "#ec4899",
  criminal: "#ef4444",
  wills_estates: "#f59e0b",
  other: "#64748b",
};

const PRACTICE_AREA_LABELS: Record<string, string> = {
  real_estate: "Real Estate",
  immigration: "Immigration",
  litigation: "Litigation",
  corporate: "Corporate",
  family: "Family",
  criminal: "Criminal",
  wills_estates: "Wills & Estates",
  other: "Other",
};

const MatterOverviewReport: React.FC<MatterOverviewReportProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-32 animate-pulse">
          <div className="h-4 w-32 bg-slate-100 rounded mb-3" />
          <div className="h-8 w-24 bg-slate-100 rounded" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-80 animate-pulse" />
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-80 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const activeMatterCount = data.byStatus.find((s) => s.status === "open")?.count || 0;

  const statusPieData = data.byStatus.map((s) => ({
    name: STATUS_LABELS[s.status] || s.status,
    value: s.count,
    color: STATUS_COLORS[s.status] || "#64748b",
  }));

  const practiceAreaChartData = data.byPracticeArea.map((pa) => ({
    name: PRACTICE_AREA_LABELS[pa.practiceArea] || pa.practiceArea,
    count: pa.count,
    color: PRACTICE_AREA_COLORS[pa.practiceArea] || "#64748b",
  }));

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Total Matters</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{data.total}</p>
            </div>
            <div className="p-3 rounded-xl bg-emerald-50">
              <Briefcase className="h-5 w-5 text-emerald-600" />
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Active Matters</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{activeMatterCount}</p>
            </div>
            <div className="p-3 rounded-xl bg-blue-50">
              <Briefcase className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Practice Areas</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{data.byPracticeArea.length}</p>
            </div>
            <div className="p-3 rounded-xl bg-purple-50">
              <Briefcase className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Matters by Status - Donut */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Matters by Status</h3>
          {statusPieData.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <Briefcase className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1.5">No matter data</h3>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
                Matter statistics will populate as cases are opened and progressed.
              </p>
            </motion.div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                  nameKey="name"
                  paddingAngle={2}
                >
                  {statusPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number | string | undefined) => [value ?? 0, "Matters"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                  }}
                />
                <Legend
                  formatter={(value: string) => (
                    <span className="text-xs text-slate-600">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Matters by Practice Area - Bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Matters by Practice Area</h3>
          {practiceAreaChartData.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-16"
            >
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <Briefcase className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1.5">No matter data</h3>
              <p className="text-sm text-slate-500 text-center max-w-sm leading-relaxed">
                Matter statistics will populate as cases are opened and progressed.
              </p>
            </motion.div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={practiceAreaChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value: number | string | undefined) => [value ?? 0, "Matters"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {practiceAreaChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MatterOverviewReport;
